import { UUID } from '../app/basicModels';

export interface MethodologyScopeIds {
  company_id?: UUID;
  workspace_id?: UUID;
}

export interface MethodologyScope {
  orgId?: UUID;
  workspaceId?: UUID;
  headers: Record<string, string>;
  queryParams: Record<string, string>;
}

export const getMethodologyScope = (ids: MethodologyScopeIds | null | undefined): MethodologyScope => {
  const orgId = ids?.company_id;
  const workspaceId = ids?.workspace_id;

  if (!orgId && !workspaceId) {
    throw new Error('getMethodologyScope: either company_id or workspace_id is required');
  }

  if (orgId) {
    return {
      orgId,
      workspaceId: undefined,
      headers: { 'x-company-id': orgId },
      queryParams: { company_id: orgId }
    };
  }

  return {
    orgId: undefined,
    workspaceId,
    headers: { 'x-workspace-id': workspaceId as string },
    queryParams: { workspace_id: workspaceId as string }
  };
};

export const applyMethodologyScopeToRequest = (
  ids: MethodologyScopeIds,
  url: string,
  headers: Record<string, string> = {}
): { url: string; headers: Record<string, string> } => {
  const scope = getMethodologyScope(ids);
  const queryString = Object.entries(scope.queryParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  const separator = url.includes('?') ? '&' : '?';
  return {
    url: queryString ? `${url}${separator}${queryString}` : url,
    headers: { ...headers, ...scope.headers }
  };
};

export const buildWorkspaceConfig = (workspaceId?: UUID): { headers: Record<string, string> } | undefined => {
  if (!workspaceId) return undefined;
  return { headers: getMethodologyScope({ workspace_id: workspaceId }).headers };
};

export const applyWorkspaceIdToPayload = <T extends { workspace_id?: UUID | null }>(payload: T, workspaceId: UUID | undefined): T =>
  workspaceId ? { ...payload, workspace_id: workspaceId } : payload;

export const isWorkspaceLevelMethodology = (
  methodology: { workspace_id?: UUID; company_id?: UUID; company?: { id?: UUID } | null } | null | undefined
): methodology is { workspace_id: UUID } => Boolean(methodology?.workspace_id) && !methodology?.company?.id && !methodology?.company_id;
